// @flow

import { getQualifiedLocation } from 'src/modules/qualified-location';
import { enrichLocation } from '@seek/seek-jobs-seo/build/location';
import { isSeekLocation } from '@seek/seek-jobs-seo';
import { forceTrackingRequest } from 'src/modules/job-tracking';
import { shouldOpenLinkExternally } from 'src/modules/navlink-rules';
import { createBrowserHistory, createMemoryHistory } from 'history';
import type { RouterHistory } from 'react-router';
import { stringify as buildQueryString } from 'query-string';
import Promise from 'bluebird';
import { metrics } from '@seek/metrics-js';
import runHooks from 'src/modules/redux-hooks';
import { openInIFrame } from 'src/modules/open-in-iframe';

export const LOCATION_CHANGED = 'LOCATION_CHANGED';
export const TRACKED_LINK_CLICKED = 'TRACKED_LINK_CLICKED';
export const CAMPAIGN_LINK_CLICKED = 'CAMPAIGN_LINK_CLICKED';
export const SET_PAGE_TITLE = 'SET_PAGE_TITLE';
export const LOCALE_CHANGED = 'LOCALE_CHANGED';
export const CUSTOM_LINK_EVENT = 'http://www.seek.com.au';
export const EXIT_LINK_EVENT = '#';

type State = {
  url: ?string,
  locale: Locale,
  pathname: ?string,
  prevPathname: ?string,
  query: ?any,
  pageNumber?: number | null,
  isHomepage: boolean,
  pageTitle?: string,
  hostname?: string,
  href?: string,
  port?: string,
  protocol?: string
};

const initialState: State = {
  url: null,
  locale: 'AU',
  pathname: null,
  prevPathname: null,
  query: null,
  pageNumber: null,
  isHomepage: true,
  pageTitle: '',
  hostname: '',
  href: '',
  port: '',
  protocol: ''
};

export const history: RouterHistory = ENV.CLIENT
  ? createBrowserHistory()
  : createMemoryHistory();

export default function reducer(
  state: State = initialState,
  action: any
): State {
  const { payload = {}, type } = action;
  switch (type) {
    case LOCATION_CHANGED: {
      const { url, location } = payload;
      const { pathname, query } = location;
      const { hostname, href, port, protocol, pathname: prevPathname } = state;

      const enrichedLocation = enrichLocation(
        {
          hostname,
          href,
          port,
          protocol,
          pathname,
          query
        },
        url
      );

      return {
        ...state,
        url,
        prevPathname,
        pageNumber: parseInt(query.page || '1', 10),
        isHomepage: pathname === '/',
        ...enrichedLocation
      };
    }

    case LOCALE_CHANGED: {
      const { locale } = payload;

      return {
        ...state,
        locale
      };
    }

    case SET_PAGE_TITLE: {
      const { pageTitle } = payload;
      return {
        ...state,
        pageTitle
      };
    }

    default:
      return state;
  }
}

export function setPageTitle(pageTitle: string) {
  return {
    type: SET_PAGE_TITLE,
    payload: {
      pageTitle
    }
  };
}

export function locationChanged(url: string, location: UrlLocation) {
  return {
    type: LOCATION_CHANGED,
    payload: {
      url,
      location
    },
    meta: {
      metrics: {
        name: LOCATION_CHANGED
      }
    }
  };
}

export function goBack() {
  return history.goBack();
}

export function localeChanged(locale: Locale) {
  return {
    type: LOCALE_CHANGED,
    payload: {
      locale
    }
  };
}

export function trackedLinkClicked(link: any) {
  const { analytics } = link;
  return {
    type: TRACKED_LINK_CLICKED,
    payload: link,
    meta: {
      metrics: {
        name: TRACKED_LINK_CLICKED,
        ...(analytics ? { tags: { link: analytics } } : {})
      }
    }
  };
}

export function campaignLinkClicked({
  linkName,
  trackingCode
}: {
  linkName: string,
  trackingCode: string
}) {
  return {
    type: CAMPAIGN_LINK_CLICKED,
    payload: {
      linkName,
      trackingCode
    },
    meta: {
      hotjar: 'Campaign Link Clicked',
      metrics: {
        name: CAMPAIGN_LINK_CLICKED
      }
    }
  };
}

const NavType = {
  NEW_WINDOW: 'NEW_WINDOW', // link opened in new window
  OUTSIDE_APP: 'OUTSIDE_APP', // link external to SPA (same window)
  INSIDE_APP: 'INSIDE_APP' // link internal to SPA
};

export const pushNavigate = ({
  location,
  preserveScroll = false
}: {
  location: any,
  preserveScroll?: boolean
}) => {
  if (preserveScroll) {
    history.replace({
      scrollTop: window.document.body.scrollTop,
      ...window.location
    });
  }

  history.push({
    ...location,
    search: location.search || buildQueryString(location.query)
  });
};

const doNavigate = (location, target, navType) => {
  if (ENV.CLIENT) {
    if (navType === NavType.INSIDE_APP) {
      pushNavigate({ location, preserveScroll: true });
    } else if (target === '_blank' && !isSeekLocation(location)) {
      openInIFrame(location.href, target);
    } else {
      window.open(location.href, target);
    }
  }
  return Promise.resolve();
};

const convertToBluebirdPromise = promise => Promise.try(promise);

const MAX_DELAY = 1000;
const ANALYTICS_DELAY = 300;

export function linkNavigate({
  analytics,
  target,
  location: nextLocation,
  preNavigationHooks
}: {
  analytics?: string,
  target?: string,
  location: any,
  preNavigationHooks?: Array<any>
}) {
  return (dispatch: *, getState: *) => {
    const { location: currentLocation } = getState();
    const { locale } = currentLocation;
    const location = getQualifiedLocation(
      locale,
      currentLocation,
      nextLocation
    );
    const isExternalLink = shouldOpenLinkExternally(location, currentLocation);

    // upon rejection, hold errors for throwing later
    let firstError = null;
    const holdFirstError = err => {
      firstError = firstError || (err instanceof Error ? err : null);
    };

    const navType =
      (target === '_blank' && NavType.NEW_WINDOW) ||
      (isExternalLink && NavType.OUTSIDE_APP) ||
      NavType.INSIDE_APP;

    const navTasks = [];

    // if new window navigate as early as possible & without delay to prevent
    // browser/ad-blockers preventing navigate (particularly firefox)
    if (navType === NavType.NEW_WINDOW) {
      doNavigate(location, target, navType);
    }

    // send off tracking analytics
    if (analytics) {
      dispatch(trackedLinkClicked({ href: location.href, analytics }));
    }

    // pre-navigation hooks
    navTasks.push(() =>
      runHooks(dispatch, preNavigationHooks).catch(holdFirstError)
    );

    // internal (SPA) navigation
    if (navType === NavType.INSIDE_APP) {
      navTasks.push(() => doNavigate(location, target, navType));
    }

    // external (outside SPA) navigation - delay navigation to ensure analytics time to send
    if (navType === NavType.OUTSIDE_APP) {
      navTasks.push(() =>
        convertToBluebirdPromise(forceTrackingRequest)
          .timeout(MAX_DELAY)
          .delay(ANALYTICS_DELAY)
          .catch(Promise.TimeoutError, () => {
            // max delay time has passed, continue to navigation
            metrics.count('navigate-outside-app-analytics-delay.timeout', [
              'timeout:true'
            ]);
          })
          .catch(holdFirstError)
          .then(() => doNavigate(location, target, navType))
      );
    }

    // run nav tasks in sequence
    return Promise.reduce(
      navTasks,
      (accumulator, currentNavTask) => currentNavTask(),
      null
    )
      .catch(holdFirstError)
      .then(() => {
        // this `then` ensures we do not return the last reduced value which makes testing more predictable
        if (firstError !== null) {
          throw firstError;
        }
      });
  };
}
